import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { contractBalanceApi } from '@/api/contractBalance'
import { contractTransactionsApi } from '@/api/contractTransactions'
import { contractPricesApi } from '@/api/contractPrices'
import { lessonSubscriptionsApi } from '@/api/lessonSubscriptions'
import { teacherPricesApi } from '@/api/teacherPrices'
import type {
  ContractBalance,
  ContractTransaction,
  LessonPaymentMethod,
  LessonSubscriptionGrant,
  LessonSubscriptionPlan,
  TransactionStatus,
  TransactionType,
} from '@/types/billing'
import { useContractRealtime } from '@/realtime/useContractRealtime'
import { getErrorMessage } from '@/utils/error'
import { formatDateDMY } from '@/utils/date'
import { formatMoney } from './formatMoney'
import { SubscriptionsBlock } from './SubscriptionsBlock'
import { BalanceDebitDialog, type BalanceDebitFormData } from './BalanceDebitDialog'

type Props = {
  contractId: number
  /** id ученика по договору — для колонки «Кем выдан» в блоке абонементов */
  studentId?: number
}

const ISSUE_METHODS: LessonPaymentMethod[] = ['from_balance', 'cash', 'deferred']

export function ContractFinanceSection({ contractId, studentId }: Props) {
  const { t } = useTranslation()
  const [balance, setBalance] = useState<ContractBalance | null>(null)
  const [transactions, setTransactions] = useState<ContractTransaction[]>([])
  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [txLoading, setTxLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<TransactionStatus | 'all'>('all')
  const [typeFilter, setTypeFilter] = useState<TransactionType | 'all'>('all')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [activeGrant, setActiveGrant] = useState<LessonSubscriptionGrant | null>(null)
  const [contractPrice, setContractPrice] = useState<string | null>(null)
  const [teacherBasePrice, setTeacherBasePrice] = useState<string | null>(null)
  const [priceHistory, setPriceHistory] = useState<string[]>([])
  const [subsRefreshKey, setSubsRefreshKey] = useState(0)
  const [debitOpen, setDebitOpen] = useState(false)
  const [issueOpen, setIssueOpen] = useState(false)
  const [plans, setPlans] = useState<LessonSubscriptionPlan[]>([])
  const [issuePlanId, setIssuePlanId] = useState<number | ''>('')
  const [issueStartsAt, setIssueStartsAt] = useState('')
  const [issueMethod, setIssueMethod] = useState<LessonPaymentMethod>('from_balance')
  const [topupOpen, setTopupOpen] = useState(false)
  const [topupAmount, setTopupAmount] = useState('')
  const [topupDescription, setTopupDescription] = useState('')
  const [priceOpen, setPriceOpen] = useState(false)
  const [priceValue, setPriceValue] = useState('')
  const [priceEffectiveFrom, setPriceEffectiveFrom] = useState('')
  const [priceError, setPriceError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState(false)

  const loadBalance = useCallback(async () => {
    const data = await contractBalanceApi.get(contractId)
    setBalance(data)
  }, [contractId])

  const loadPrices = useCallback(async () => {
    const data = await contractPricesApi.index(contractId)
    setContractPrice(
      data.active ? `${data.active.price_per_hour} ${data.active.currency}/ч` : null
    )
    setPriceHistory(
      data.history.map(
        (p) => `${formatDateDMY(p.effective_from)}: ${p.price_per_hour} ${p.currency}/ч`
      )
    )
    // Индивидуальная цена не задана — показываем базовую цену учителя
    if (!data.active) {
      const teacherPrices = await teacherPricesApi.index()
      setTeacherBasePrice(
        teacherPrices.active
          ? `${teacherPrices.active.price_per_hour} ${teacherPrices.active.currency}/ч`
          : null
      )
    } else {
      setTeacherBasePrice(null)
    }
  }, [contractId])

  const handleSetPrice = async () => {
    const value = Number(priceValue)
    // Ноль допустим: бартер, родственники, пробное занятие. Бэкенд принимает
    // его давно (min:0), резала только эта проверка. А вот пустое поле нулём
    // считать нельзя: Number('') === 0, и «Сохранить» без ввода молча
    // выставило бы ученику бесплатные уроки.
    if (priceValue.trim() === '' || !Number.isFinite(value) || value < 0) {
      setPriceError(t('billing.priceInvalid', 'Укажите корректную цену за час'))
      return
    }
    setPriceError(null)
    setActionLoading(true)
    try {
      await contractPricesApi.store(contractId, {
        price_per_hour: value,
        ...(priceEffectiveFrom ? { effective_from: priceEffectiveFrom } : {}),
      })
      setPriceOpen(false)
      await loadPrices()
    } catch (e) {
      setPriceError(getErrorMessage(e) || t('billing.priceSaveError', 'Не удалось сохранить цену'))
    } finally {
      setActionLoading(false)
    }
  }

  const loadSubscriptions = useCallback(async () => {
    const data = await lessonSubscriptionsApi.listForContract(contractId, { page: 1, per_page: 1 })
    setActiveGrant(data.current)
  }, [contractId])

  const loadTransactions = useCallback(
    async (pageNum: number, append: boolean) => {
      setTxLoading(true)
      try {
        const res = await contractTransactionsApi.index(contractId, {
          page: pageNum,
          per_page: 20,
          status: statusFilter === 'all' ? undefined : statusFilter,
          type: typeFilter === 'all' ? undefined : typeFilter,
        })
        setTransactions((prev) => (append ? [...prev, ...res.data] : res.data))
        setPage(res.current_page)
        setLastPage(res.last_page)
      } finally {
        setTxLoading(false)
      }
    },
    [contractId, statusFilter, typeFilter]
  )

  const reloadAll = useCallback(async () => {
    setError(null)
    try {
      await Promise.all([loadBalance(), loadPrices(), loadSubscriptions(), loadTransactions(1, false)])
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [loadBalance, loadPrices, loadSubscriptions, loadTransactions, t])

  useEffect(() => {
    setLoading(true)
    reloadAll().catch(() => {})
  }, [reloadAll])

  useEffect(() => {
    if (!loading) {
      void loadTransactions(1, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter])

  useContractRealtime(contractId, () => {
    reloadAll().catch(() => {})
    setSubsRefreshKey((v) => v + 1)
  })

  const pendingTransactions = useMemo(
    () => transactions.filter((tx) => tx.status === 'pending'),
    [transactions]
  )

  const handleToggleNotifications = async (enabled: boolean) => {
    setActionLoading(true)
    try {
      const updated = await contractBalanceApi.updateSettings(contractId, {
        payment_notifications_enabled: enabled,
      })
      setBalance(updated)
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setActionLoading(false)
    }
  }

  const handleApprove = async (txId: number) => {
    try {
      await contractTransactionsApi.approve(txId)
      await loadTransactions(1, false)
      await loadBalance()
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    }
  }

  const handleReject = async (txId: number) => {
    try {
      await contractTransactionsApi.reject(txId)
      await loadTransactions(1, false)
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    }
  }

  const openIssueModal = async () => {
    try {
      const res = await lessonSubscriptionsApi.listPlans({
        is_active: true,
        subscription_type: 'individual',
      })
      setPlans(res.data)
      setIssuePlanId(res.data[0]?.id ?? '')
      setIssueStartsAt(new Date().toISOString().slice(0, 10))
      setIssueOpen(true)
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    }
  }

  const handleIssueSubscription = async () => {
    if (!issuePlanId || !issueStartsAt) return
    setActionLoading(true)
    try {
      await lessonSubscriptionsApi.issueForContract(contractId, {
        plan_id: Number(issuePlanId),
        starts_at: issueStartsAt,
        payment_method: issueMethod,
      })
      setIssueOpen(false)
      setSubsRefreshKey((v) => v + 1)
      await loadSubscriptions()
      await loadBalance()
      await loadTransactions(1, false)
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setActionLoading(false)
    }
  }

  const handleDebit = async (data: BalanceDebitFormData) => {
    try {
      await contractTransactionsApi.correction(contractId, {
        amount: data.amount,
        direction: 'debit',
        description: data.description,
      })
      setDebitOpen(false)
      await loadBalance()
      await loadTransactions(1, false)
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    }
  }

  const handleTopup = async () => {
    const amount = parseFloat(topupAmount)
    if (!Number.isFinite(amount) || amount <= 0) return
    setActionLoading(true)
    try {
      await contractTransactionsApi.topup(contractId, {
        amount,
        description: topupDescription || undefined,
      })
      setTopupOpen(false)
      setTopupAmount('')
      setTopupDescription('')
      await loadBalance()
      await loadTransactions(1, false)
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setActionLoading(false)
    }
  }

  if (loading && !balance) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <LinearProgress />
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('billing.financeSection', 'Финансы')}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('billing.balance', 'Баланс')}
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {formatMoney(balance?.balance_amount ?? 0, balance?.currency ?? 'RUB')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('billing.individualPrice', 'Индивидуальная цена')}
              </Typography>
              <Typography variant="body1">{contractPrice ?? t('billing.noIndividualPrice', 'Не задана')}</Typography>
              {!contractPrice && teacherBasePrice && (
                <Typography variant="caption" color="text.secondary">
                  {t('billing.teacherBasePriceLabel', 'Базовая цена учителя: {{price}}', {
                    price: teacherBasePrice,
                  })}
                </Typography>
              )}
            </Box>
            {activeGrant && (
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('billing.activeSubscription', 'Активный абонемент')}
                </Typography>
                <Typography variant="body2">{activeGrant.plan?.title ?? `#${activeGrant.id}`}</Typography>
                <LinearProgress
                  variant="determinate"
                  value={
                    activeGrant.lessons_total > 0
                      ? ((activeGrant.lessons_total - activeGrant.lessons_remaining) / activeGrant.lessons_total) * 100
                      : 0
                  }
                  sx={{ mt: 1, height: 8, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {t('billing.lessonsRemaining', '{{remaining}} из {{total}}', {
                    remaining: activeGrant.lessons_remaining,
                    total: activeGrant.lessons_total,
                  })}
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Button size="small" variant="outlined" onClick={() => setTopupOpen(true)}>
              {t('billing.topup', 'Пополнить')}
            </Button>
            <Button size="small" variant="outlined" color="error" onClick={() => setDebitOpen(true)}>
              {t('billing.debit', 'Списать')}
            </Button>
            <Button size="small" variant="outlined" onClick={() => void openIssueModal()}>
              {t('billing.issueSubscription', 'Выдать абонемент')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setPriceError(null)
                setPriceValue('')
                setPriceEffectiveFrom('')
                setPriceOpen(true)
              }}
            >
              {t('billing.setIndividualPrice', 'Задать цену')}
            </Button>
            <Button size="small" onClick={() => setFiltersOpen((v) => !v)}>
              {t('billing.filters', 'Фильтры')}
            </Button>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={balance?.settings.payment_notifications_enabled ?? true}
                onChange={(e) => void handleToggleNotifications(e.target.checked)}
                disabled={actionLoading}
              />
            }
            label={t('billing.paymentNotifications', 'Уведомления об оплате')}
          />

          {priceHistory.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('billing.priceHistory', 'История цены')}
              </Typography>
              {priceHistory.slice(0, 5).map((line) => (
                <Typography key={line} variant="caption" display="block">
                  {line}
                </Typography>
              ))}
            </Box>
          )}

          {pendingTransactions.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('billing.pendingTransactions', 'Ожидают подтверждения')}
              </Typography>
              {pendingTransactions.map((tx) => (
                <Box
                  key={tx.id}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {formatMoney(tx.amount, tx.currency)} — {tx.description || tx.type}
                  </Typography>
                  <Button size="small" color="success" onClick={() => void handleApprove(tx.id)}>
                    {t('billing.approve', 'Подтвердить')}
                  </Button>
                  <Button size="small" color="error" onClick={() => void handleReject(tx.id)}>
                    {t('billing.reject', 'Отклонить')}
                  </Button>
                </Box>
              ))}
            </Box>
          )}

          <Collapse in={filtersOpen}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>{t('billing.filterStatus', 'Статус')}</InputLabel>
                <Select
                  label={t('billing.filterStatus', 'Статус')}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as TransactionStatus | 'all')}
                >
                  <MenuItem value="all">{t('common.all', 'Все')}</MenuItem>
                  <MenuItem value="pending">{t('billing.txStatus.pending', 'Ожидает')}</MenuItem>
                  <MenuItem value="approved">{t('billing.txStatus.approved', 'Подтверждена')}</MenuItem>
                  <MenuItem value="rejected">{t('billing.txStatus.rejected', 'Отклонена')}</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>{t('billing.filterType', 'Тип')}</InputLabel>
                <Select
                  label={t('billing.filterType', 'Тип')}
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value as TransactionType | 'all')}
                >
                  <MenuItem value="all">{t('common.all', 'Все')}</MenuItem>
                  <MenuItem value="topup">{t('billing.txType.topup', 'Пополнение')}</MenuItem>
                  <MenuItem value="lesson_payment">{t('billing.txType.lesson_payment', 'Оплата урока')}</MenuItem>
                  <MenuItem value="subscription_purchase">{t('billing.txType.subscription_purchase', 'Абонемент')}</MenuItem>
                  <MenuItem value="correction">{t('billing.txType.correction', 'Корректировка')}</MenuItem>
                  <MenuItem value="refund">{t('billing.txType.refund', 'Возврат')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Collapse>

          <TableContainer sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('billing.txDate', 'Дата')}</TableCell>
                  <TableCell>{t('billing.txTypeLabel', 'Тип')}</TableCell>
                  <TableCell>{t('billing.txAmount', 'Сумма')}</TableCell>
                  <TableCell>{t('billing.filterStatus', 'Статус')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((tx) => (
                  <TableRow key={tx.id}>
                    <TableCell>{formatDateDMY(tx.created_at)}</TableCell>
                    <TableCell>{t(`billing.txType.${tx.type}`, tx.type)}</TableCell>
                    <TableCell>
                      {tx.direction === 'debit' ? '−' : '+'}
                      {formatMoney(tx.amount, tx.currency)}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={t(`billing.txStatus.${tx.status}`, tx.status)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {page < lastPage && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button disabled={txLoading} onClick={() => void loadTransactions(page + 1, true)}>
                {t('billing.loadMore', 'Загрузить ещё')}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <SubscriptionsBlock
        contractId={contractId}
        studentId={studentId}
        refreshKey={subsRefreshKey}
        onChanged={() => {
          void loadBalance()
          void loadSubscriptions()
          void loadTransactions(1, false)
        }}
      />

      <BalanceDebitDialog open={debitOpen} onClose={() => setDebitOpen(false)} onSubmit={handleDebit} />

      <Dialog open={issueOpen} onClose={() => setIssueOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('billing.issueSubscription', 'Выдать абонемент')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('billing.plan', 'Абонемент')}</InputLabel>
              <Select
                label={t('billing.plan', 'Абонемент')}
                value={issuePlanId}
                onChange={(e) => setIssuePlanId(Number(e.target.value))}
              >
                {plans.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.title} ({p.lessons_count} {t('billing.lessonsShort', 'ур.')})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="date"
              label={t('billing.startsAt', 'Начало')}
              InputLabelProps={{ shrink: true }}
              value={issueStartsAt}
              onChange={(e) => setIssueStartsAt(e.target.value)}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>{t('billing.paymentMethod', 'Способ оплаты')}</InputLabel>
              <Select
                label={t('billing.paymentMethod', 'Способ оплаты')}
                value={issueMethod}
                onChange={(e) => setIssueMethod(e.target.value as LessonPaymentMethod)}
              >
                {ISSUE_METHODS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {t(`billing.paymentMethodOption.${m}`, m)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueOpen(false)}>{t('common.cancel', 'Отмена')}</Button>
          <Button variant="contained" disabled={actionLoading} onClick={() => void handleIssueSubscription()}>
            {t('common.save', 'Сохранить')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={topupOpen} onClose={() => setTopupOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('billing.topup', 'Пополнить')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              size="small"
              type="number"
              label={t('billing.amount', 'Сумма')}
              value={topupAmount}
              onChange={(e) => setTopupAmount(e.target.value)}
            />
            <TextField
              size="small"
              label={t('billing.description', 'Комментарий')}
              value={topupDescription}
              onChange={(e) => setTopupDescription(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTopupOpen(false)}>{t('common.cancel', 'Отмена')}</Button>
          <Button variant="contained" disabled={actionLoading} onClick={() => void handleTopup()}>
            {t('common.save', 'Сохранить')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={priceOpen} onClose={() => setPriceOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('billing.setIndividualPriceTitle', 'Индивидуальная цена ученика')}</DialogTitle>
        <DialogContent>
          {priceError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPriceError(null)}>
              {priceError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              size="small"
              type="number"
              label={t('billing.pricePerHour', 'Цена за час, ₽')}
              value={priceValue}
              onChange={(e) => setPriceValue(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
            />
            <TextField
              size="small"
              type="date"
              label={t('billing.priceEffectiveFrom', 'Действует с')}
              value={priceEffectiveFrom}
              onChange={(e) => setPriceEffectiveFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={t('billing.priceEffectiveFromHint', 'Пусто — с сегодняшнего дня')}
            />
            <Typography variant="caption" color="text.secondary">
              {t(
                'billing.setIndividualPriceHint',
                'Заменяет базовую ставку учителя для этого ученика. История сохраняется.'
              )}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceOpen(false)}>{t('common.cancel', 'Отмена')}</Button>
          <Button variant="contained" disabled={actionLoading} onClick={() => void handleSetPrice()}>
            {t('common.save', 'Сохранить')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
